import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../../hooks/useMe';
import { reauth } from '../../lib/me';
import { biometricSupported, runBiometric } from '../../lib/biometric';
import { haptic } from '../../hooks/useTelegram';
import { FigIcon } from '../FigIcon';
import { Numpad, PinDots } from './Numpad';

const PIN_LEN = 4;

/**
 * Re-authentication gate. Prompts biometric (when the device actually supports
 * it) or the standard PIN pad, then calls `onAuthed` with a fresh reauth token
 * (undefined when no PIN/biometric is set, e.g. dev). `onAuthed` returns false to
 * signal the guarded action failed → the gate shows an error and lets the user
 * retry. The PIN pad here is identical to the PIN-set screen's.
 */
export function ReauthGate({
  onAuthed,
  bioTitle,
  bioHint,
}: {
  onAuthed: (reauthToken?: string) => Promise<boolean>;
  bioTitle: string;
  bioHint: string;
}) {
  const { t } = useTranslation();
  const { me, token } = useMe();
  const pinSet = me?.pinSet ?? false;
  const bio = me?.biometricEnabled ?? false;
  const needsAuth = pinSet || bio;

  const [entry, setEntry] = useState('');
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bioFailed, setBioFailed] = useState(false);
  // null = still probing biometric hardware (runs in the background so a client
  // without Face ID — Telegram Desktop — shows the PIN pad immediately).
  const [bioSupported, setBioSupported] = useState<boolean | null>(null);
  const canBio = bioSupported === true;
  const verifying = useRef(false);
  const triedBio = useRef(false);
  const done = useRef(false);

  const succeed = useCallback(
    async (rt?: string): Promise<boolean> => {
      if (done.current) return true;
      const ok = await onAuthed(rt);
      if (ok) done.current = true;
      return ok;
    },
    [onAuthed],
  );

  useEffect(() => {
    if (!me) return; // wait until we know whether biometric is enabled
    if (!bio) {
      setBioSupported(false);
      return;
    }
    let alive = true;
    biometricSupported().then((ok) => alive && setBioSupported(ok));
    return () => {
      alive = false;
    };
  }, [me, bio]);

  const tryBiometric = useCallback(async () => {
    const ok = await runBiometric(t('biometricFlow.reason')).catch(() => false);
    if (!ok || !token) {
      setBioFailed(true); // cancelled/unavailable → fall back to the PIN pad
      return;
    }
    setLoading(true);
    try {
      const { token: rt } = await reauth(token, 'biometric');
      if (!(await succeed(rt))) setBioFailed(true);
    } catch {
      setBioFailed(true);
    } finally {
      setLoading(false);
    }
  }, [token, t, succeed]);

  // On open: no reauth needed (or dev, no token) → proceed; biometric supported
  // → prompt it; PIN-only → the pad below handles it. CRUCIAL: wait for `me`
  // before auto-passing — until it loads, pinSet/biometricEnabled default to
  // false, so skipping this guard would silently unlock every gate at app launch
  // (before we know a PIN is set) instead of prompting.
  useEffect(() => {
    if (done.current) return;
    if (!token) {
      void succeed(undefined); // dev / no session → nothing to authenticate against
      return;
    }
    if (!me) return; // still loading — don't decide until pin/biometric status is known
    if (!needsAuth) {
      void succeed(undefined);
    } else if (canBio && !triedBio.current) {
      triedBio.current = true;
      void tryBiometric();
    }
  }, [token, me, needsAuth, canBio, succeed, tryBiometric]);

  const pressPin = (d: string) => {
    if (verifying.current) return;
    haptic();
    setError(false);
    setEntry((p) => (p.length >= PIN_LEN ? p : p + d));
  };
  const backspace = () => setEntry((e) => e.slice(0, -1));

  useEffect(() => {
    if (entry.length !== PIN_LEN || verifying.current || !token) return;
    verifying.current = true;
    setLoading(true);
    void (async () => {
      try {
        const { token: rt } = await reauth(token, 'pin', entry);
        if (!(await succeed(rt))) throw new Error('failed');
      } catch {
        haptic();
        setError(true);
        setLoading(false);
        setTimeout(() => {
          setEntry('');
          setError(false);
          verifying.current = false;
        }, 850);
        return;
      }
      setLoading(false);
      verifying.current = false;
    })();
  }, [entry, token, succeed]);

  const spinner = (
    <div className="push-enter flex min-h-screen items-center justify-center bg-card">
      <span className="size-9 animate-spin rounded-full border-[3px] border-border border-t-accent" />
    </div>
  );

  // Wait for /me, auto-pass when no PIN/biometric, or probe Face ID support —
  // never fall through to the PIN pad in these cases (users without a PIN were
  // seeing "Enter PIN" while succeed(undefined) ran in the background).
  if (
    (token && !me) ||
    !token ||
    (me && !needsAuth) ||
    (needsAuth && bio && bioSupported === null)
  ) {
    return spinner;
  }

  // Biometric path: Face ID prompt with a descriptive waiting screen (no PIN pad).
  if (canBio && !bioFailed) {
    return (
      <div className="push-enter flex min-h-screen flex-col items-center justify-center gap-6 bg-card px-8 text-center">
        <FigIcon name="pfFaceId" size={40} />
        <div className="flex flex-col items-center gap-3">
          <p className="text-[20px] font-medium leading-tight text-ink">{bioTitle}</p>
          <p className="max-w-[260px] text-[15px] leading-[1.44] text-muted">{bioHint}</p>
        </div>
        <span className="mt-2 size-8 animate-spin rounded-full border-[3px] border-border border-t-accent" />
        {pinSet && (
          <button
            onClick={() => {
              haptic();
              setBioFailed(true);
            }}
            className="mt-2 text-[15px] font-medium text-accent active:opacity-70"
          >
            {t('security.enterPin')}
          </button>
        )}
      </div>
    );
  }

  // PIN pad only when a PIN is actually set (biometric-only fallback after cancel).
  if (!pinSet) return spinner;

  const color = error ? 'rgb(var(--negative))' : 'rgb(var(--accent))';
  return (
    <div className="push-enter relative flex min-h-screen flex-col bg-card">
      <div className="flex flex-1 flex-col items-center justify-center px-4">
        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col items-center gap-8" style={{ color: error ? color : 'rgb(var(--ink))' }}>
            <FigIcon name="pfLock" size={24} />
            <p className="text-[20px] font-medium leading-none">{t('security.enterPin')}</p>
          </div>
          <PinDots len={PIN_LEN} count={entry.length} color={color} />
        </div>
        <div className="mt-16 grid place-items-center" style={{ minHeight: 340 }}>
          {loading ? (
            <span className="size-9 animate-spin rounded-full border-[3px] border-border border-t-accent" />
          ) : (
            <Numpad onPress={pressPin} onBackspace={backspace} />
          )}
        </div>
      </div>
    </div>
  );
}
